if (!customElements.get('engraving-selector')) {
  customElements.define(
    'engraving-selector',
    class EngravingSelector extends HTMLElement {
      static ALLOWED_CHARS_PATTERN = /[^A-Za-z0-9 !?"'~()^ç.:,;-]/g;
      static ERROR_TIMEOUT_MS = 3000;

      constructor() {
        super();

        this.state = this.createInitialState();
        this.config = this.parseConfig();
        this.errorTimeout = null;
        this._charErrorTimeout = null;
        this.addToCartButtons = null;

        this.initializeFromDOM();
        this.bindEvents();
        this.updateUI();
        this.updateAddToCartButtons();
      }

      createInitialState() {
        return {
          enabled: false,
          sides: 0,
          font: '',
          front: { text: '' },
          back: { text: '' }
        };
      }

      parseConfig() {
        return {
          sectionId: this.dataset.sectionId,
          supportsTwoSides: this.dataset.supportsTwoSides === 'true',
          charLimit: parseInt(this.dataset.charLimit, 10) || 9,
          charLimitBack: parseInt(this.dataset.charLimitBack, 10) || parseInt(this.dataset.charLimit, 10) || 9
        };
      }

      initializeFromDOM() {
        const checkedToggle = this.querySelector('[data-engraving-toggle]:checked');
        if (checkedToggle) {
          this.state.enabled = checkedToggle.dataset.engravingValue === 'enabled';
          this.state.sides = parseInt(checkedToggle.dataset.sidesValue, 10) || 0;
        }

        const checkedFont = this.querySelector('[data-engraving-font]:checked');
        if (checkedFont) {
          this.state.font = checkedFont.dataset.fontValue;
        }
      }

      bindEvents() {
        const eventMap = [
          { selector: '[data-engraving-toggle]', event: 'change', handler: this.handleToggleChange },
          { selector: '[data-engraving-font]', event: 'change', handler: this.handleFontChange },
          { selector: '[data-engraving-text]', event: 'input', handler: this.handleTextInput }
        ];

        eventMap.forEach(({ selector, event, handler }) => {
          this.querySelectorAll(selector).forEach(input => {
            input.addEventListener(event, handler.bind(this));
          });
        });
      }

      connectedCallback() {
        const form = document.getElementById(`product-form-${this.config.sectionId}`);
        if (form) {
          form.addEventListener('submit', this.handleFormSubmit.bind(this));
        }
        this.updateFieldsDisabledState();
      }

      handleToggleChange(event) {
        const { engravingValue, sidesValue } = event.target.dataset;

        this.state.enabled = engravingValue === 'enabled';
        this.state.sides = parseInt(sidesValue, 10) || 0;

        ['front', 'back'].forEach(side => this.resetSide(side));
        this.clearFontSelection();

        this.clearAllErrors();
        this.updateUI();
        this.updateFieldsDisabledState();
        this.updateAddToCartButtons();
        this.updatePreview();
      }

      handleFontChange(event) {
        this.state.font = event.target.dataset.fontValue;
        this.clearFieldError('font');
        this.updateFontOnInputs();
        this.updateAddToCartButtons();
        this.updatePreview();
      }

      handleTextInput(event) {
        const side = event.target.dataset.engravingText;

        const rawText = event.target.value;
        const filtered = rawText.replace(EngravingSelector.ALLOWED_CHARS_PATTERN, '');

        if (filtered !== rawText) {
          event.target.value = filtered;
          this.showInvalidCharError(side);
        }

        this.state[side].text = filtered;
        this.updateCharCounter(event.target, filtered.length);

        if (filtered.trim()) {
          this.clearFieldError('text', side);
        }

        this.updateAddToCartButtons();
        this.updatePreview();
      }

      showInvalidCharError(side) {
        if (this._charErrorTimeout) {
          clearTimeout(this._charErrorTimeout);
          this._charErrorTimeout = null;
        }

        const errorEl = this.querySelector(`[data-invalid-char-error="${side}"]`);
        if (errorEl) {
          errorEl.hidden = false;
          this._charErrorTimeout = setTimeout(() => this.clearInvalidCharError(side), 2000);
        }
      }

      clearInvalidCharError(side) {
        const errorEl = this.querySelector(`[data-invalid-char-error="${side}"]`);
        if (errorEl) {
          errorEl.hidden = true;
        }
      }

      handleFormSubmit(event) {
        if (!this.state.enabled) return;

        const validationResult = this.validateEngraving();
        if (!validationResult.valid) {
          event.preventDefault();
          this.showValidationErrors(validationResult.errors);
        }
      }

      getCharLimit(side) {
        return side === 'back' ? this.config.charLimitBack : this.config.charLimit;
      }

      resetSide(side) {
        this.clearSideFields(side);
      }

      clearSideFields(side) {
        this.clearTextInput(side);
      }

      clearTextInput(side) {
        const textInput = this.querySelector(`[data-engraving-text="${side}"]`);
        if (textInput) {
          textInput.value = '';
          this.state[side].text = '';
          this.updateCharCounter(textInput, 0);
        }
      }

      clearFontSelection() {
        const fontInputs = this.querySelectorAll('[data-engraving-font]');
        fontInputs.forEach((input, index) => {
          input.checked = index === 0;
          if (index === 0) {
            this.state.font = input.dataset.fontValue;
          }
        });
        this.updateFontOnInputs();
      }

      updateFontOnInputs() {}

      updatePreview() {
        if (!this.state.enabled) {
          this.querySelectorAll('[data-engraving-preview]').forEach(el => { el.hidden = true; });
          return;
        }

        ['front', 'back'].forEach(side => {
          const previewEl = this.querySelector(`[data-engraving-preview="${side}"]`);
          if (!previewEl) return;
          const text = this.state[side].text.trim();
          previewEl.hidden = !text;
          if (!text) return;
          const textEl = previewEl.querySelector(`[data-preview-text="${side}"]`);
          if (!textEl) return;
          textEl.textContent = this.state[side].text;
          textEl.className = textEl.className.replace(/engraving-font--\S+/g, '').trim();
          if (this.state.font) textEl.classList.add(`engraving-font--${this.state.font}`);
        });
      }

      updateUI() {
        this.updateContainerVisibility();
        ['front', 'back'].forEach(side => this.updateTextInputCharLimit(side));
        this.updateFontOnInputs();
        this.updatePreview();
      }

      updateContainerVisibility() {
        const optionsContainer = this.querySelector('[data-engraving-options]');
        if (optionsContainer) {
          optionsContainer.hidden = !this.state.enabled;
        }

        const frontSide = this.querySelector('[data-engraving-side="front"]');
        if (frontSide) {
          frontSide.hidden = this.state.sides < 1;
        }

        const backSide = this.querySelector('[data-engraving-side="back"]');
        if (backSide) {
          backSide.hidden = this.state.sides < 2;
        }
      }

      updateTextInputCharLimit(side) {
        const charLimit = this.getCharLimit(side);
        const textInput = this.querySelector(`[data-engraving-text="${side}"]`);
        const charLimitDisplay = this.querySelector(`[data-text-input="${side}"] [data-char-limit]`);

        if (textInput) {
          textInput.setAttribute('maxlength', charLimit);

          if (textInput.value.length > charLimit) {
            textInput.value = textInput.value.substring(0, charLimit);
            this.state[side].text = textInput.value;
            this.updateCharCounter(textInput, textInput.value.length);
          }
        }

        if (charLimitDisplay) {
          charLimitDisplay.textContent = charLimit;
        }
      }

      updateCharCounter(textInput, count) {
        const counter = textInput.closest('.engraving-text-input')?.querySelector('[data-char-count]');
        if (counter) {
          counter.textContent = count;
        }
      }

      updateFieldsDisabledState() {
        const formId = `product-form-${this.config.sectionId}`;

        ['front', 'back'].forEach(side => {
          const sideEnabled = (side === 'front' && this.state.sides >= 1) ||
                              (side === 'back' && this.state.sides >= 2);
          this.updateTextFieldState(side, sideEnabled, formId);
        });

        const fontEnabled = this.state.enabled;
        this.querySelectorAll('[data-engraving-font]').forEach(field => {
          this.setFieldState(field, fontEnabled, formId);
        });
      }

      updateTextFieldState(side, enabled, formId) {
        this.querySelectorAll(`[data-engraving-side="${side}"] [data-engraving-text="${side}"]`).forEach(field => {
          this.setFieldState(field, enabled, formId);
        });
      }

      setFieldState(field, enabled, formId) {
        if (enabled) {
          field.removeAttribute('disabled');
          field.setAttribute('form', formId);
        } else {
          field.setAttribute('disabled', '');
          field.removeAttribute('form');
        }
      }

      validateEngraving() {
        if (!this.state.enabled) return { valid: true, errors: [] };

        const errors = [];

        if (this.state.sides === 2) {
          const frontValid = this.isSideValid('front');
          const backValid = this.isSideValid('back');
          if (!frontValid && !backValid) {
            errors.push(...this.validateSide('front'), ...this.validateSide('back'));
          }
        } else {
          errors.push(...this.validateSide('front'));
        }

        errors.push(...this.validateFont());

        return { valid: errors.length === 0, errors };
      }

      validateSide(side) {
        const { text } = this.state[side];
        const errors = [];
        if (!text?.trim()) {
          errors.push({ field: 'text', side });
        }
        return errors;
      }

      validateFont() {
        if (!this.state.font) {
          return [{ field: 'font', side: null }];
        }
        return [];
      }

      isSideValid(side) {
        return this.validateSide(side).length === 0;
      }

      isFormValid() {
        if (!this.state.enabled) return true;
        if (this.state.sides === 2) {
          if (!this.isSideValid('front') && !this.isSideValid('back')) return false;
        } else {
          if (!this.isSideValid('front')) return false;
        }
        if (!this.state.font) return false;
        return true;
      }

      showValidationErrors(errors) {
        this.clearErrorTimeout();
        errors.forEach(({ field, side }) => this.showFieldError(field, side));
        this.errorTimeout = setTimeout(() => this.clearAllErrors(), EngravingSelector.ERROR_TIMEOUT_MS);
      }

      showFieldError(field, side) {
        const element = field === 'font'
          ? this.querySelector('[data-font-selector]')
          : this.querySelector(`[data-engraving-text="${side}"]`);
        if (element) element.classList.add('engraving-field--error');
      }

      clearFieldError(field, side) {
        const element = field === 'font'
          ? this.querySelector('[data-font-selector]')
          : this.querySelector(`[data-engraving-text="${side}"]`);
        if (element) element.classList.remove('engraving-field--error');
      }

      clearAllErrors() {
        this.clearErrorTimeout();
        this.querySelectorAll('.engraving-field--error').forEach(el => {
          el.classList.remove('engraving-field--error');
        });
      }

      clearErrorTimeout() {
        if (this.errorTimeout) {
          clearTimeout(this.errorTimeout);
          this.errorTimeout = null;
        }
      }

      getAddToCartButtons() {
        if (!this.addToCartButtons) {
          this.addToCartButtons = Array.from(document.querySelectorAll('.add-to-cart'));
        }
        return this.addToCartButtons;
      }

      updateAddToCartButtons() {
        const isValid = this.isFormValid();
        this.getAddToCartButtons().forEach(button => {
          button.toggleAttribute('disabled', !isValid);
          button.classList.toggle('disabled', !isValid);
        });
      }
    }
  );
}
